import { useState, type FormEvent } from 'react';

interface Props {
  onAuthenticated: () => void;
}

export default function GateAuth({ onAuthenticated }: Props) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await fetch('/api/gate/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          onAuthenticated();
        }
      } else {
        setError('Invalid password');
      }
    } catch {
      setError('Authentication failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-5 relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none bg-grid-pattern bg-grid"
        style={{
          maskImage:
            'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
        }}
      />

      {/* Glow blobs */}
      <div className="absolute -top-48 -left-24 w-[600px] h-[600px] rounded-full bg-accent opacity-10 blur-[120px] animate-pulse-slow" />
      <div className="absolute -bottom-48 -right-24 w-[600px] h-[600px] rounded-full bg-purple-500 opacity-[0.08] blur-[120px] animate-pulse-slow" />

      {/* Card */}
      <div className="w-full max-w-[400px] bg-dark-card backdrop-blur-xl border border-dark-border rounded-2xl px-8 py-10 shadow-2xl relative z-10">
        {/* Icon */}
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-[14px] bg-gradient-to-br from-accent to-purple-500 mb-4 shadow-lg shadow-accent/20">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <h1 className="text-[22px] font-bold text-white mb-1 tracking-tight">
            CodeForge
          </h1>
          <p className="text-sm text-gray-500">
            Enter password to access the platform
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 px-3.5 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f87171"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label
              htmlFor="password"
              className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider font-mono"
            >
              Password
            </label>
            <div className="relative">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                className="absolute left-3 top-1/2 -translate-y-1/2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
                className="input-field !pl-10"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !password}
            className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {isLoading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Authenticating...
              </>
            ) : (
              'Access Portal'
            )}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-dark-border text-center">
          <p className="text-[11px] text-gray-600 font-mono tracking-wider">
            CodeForge — AI-Powered Project Scaffolding
          </p>
        </div>
      </div>
    </div>
  );
}
